import { HttpClient } from '@angular/common/http';
import {
  AnonymousCredential,
  BlobItem,
  BlobServiceClient,
  ContainerClient,
  ContainerListBlobsOptions
} from '@azure/storage-blob';
import { map, Observable } from 'rxjs';
import { AppConfig } from '../../app-services/config/config.app';
import { AppMonitoring } from '../../app-services/monitoring/monitor.app';
import { Enums } from '../../enums/enums';
import { ModelCampaignMetaData } from '../../models/azure-storage/azure-storage-blob-metadata.model';
import { ModelAzureStorageInfo } from '../../models/azure-storage/azure-storage-info.model';
import { ModelCampaignListDetails } from '../../models/campaign/campaign-list-details.model';
import { ModelCampaign } from '../../models/campaign/campaign.model';
import { ModelRedirectCampaign } from '../../models/campaign/redirect-campaign.model';
import { AbstractEndpoints } from '../endpoints/endpoints.service.abstract';
import { AbstractServiceAzureStorage } from './azure-storage.service.abstract';
import { ModelMenu } from '../../models/menu/menu.model';
import { AbstractAppAuthentication } from '../../app-services/authentication/authentication.app.abstract';
import { inject } from '@angular/core';
import { ModelAzureStorageFileUploadResponse } from '../../models/azure-storage/azure-storage-file-upload-response.model';
import { ModelSubscriptionRecord } from '../../models/subscription/subscription-record.model';
import { ModelInitializer } from '../../models/model-initializer.helper';

export class ServiceAzureStorage implements AbstractServiceAzureStorage {
  private readonly appMonitor = inject(AppMonitoring);

  private readonly campaigns: ModelCampaignListDetails[] = [];

  private _containerClient: ContainerClient | undefined;
  public get containerClient(): ContainerClient {
    if (!this._containerClient) {
      const blobServiceClient = new BlobServiceClient(
        this.appConfig.azureStorage.storageURL,
        new AnonymousCredential()
      );
      this._containerClient = blobServiceClient.getContainerClient(this.appConfig.azureStorage.baseContainerName);
    }
    return this._containerClient;
  }

  constructor(
    private readonly endpoints: AbstractEndpoints,
    private readonly http: HttpClient,
    private readonly appConfig: AppConfig
  ) {}

  initializeBlobStorageCRUDClient(containerName?: string): Observable<BlobServiceClient> {
    this.appMonitor.logEvent(this, 'Blob storage CRUD client initializing', {
      containerName,
      url: this.endpoints.token.getSASCRUDToken(containerName)
    });
    return this.http.get<ModelAzureStorageInfo>(this.endpoints.token.getSASCRUDToken(containerName)).pipe(
      map((result: ModelAzureStorageInfo) => {
        this.appMonitor.logEvent(this, 'Blob storage CRUD client initialized', { containerName });
        return new BlobServiceClient(`${result.storageUri}${result.storageAccessToken}`);
      })
    );
  }

  initializeBlobStorageAnonymousCRUDClient(containerName?: string, folder?: string): Observable<BlobServiceClient> {
    this.appMonitor.logEvent(this, 'Blob storage Anonymous CRUD client initializing', {
      containerName,
      folder,
      url: this.endpoints.token.getSASTokenForFolder(containerName, folder)
    });
    return this.http.get<ModelAzureStorageInfo>(this.endpoints.token.getSASTokenForFolder(containerName, folder)).pipe(
      map((result: ModelAzureStorageInfo) => {
        this.appMonitor.logEvent(this, 'Blob storage Anonymous CRUD client initialized', { containerName, folder });
        return new BlobServiceClient(`${result.storageUri}${result.storageAccessToken}`);
      })
    );
  }

  initializeBlobStorageAppendSubscriptionClient(fileName: string): Observable<BlobServiceClient> {
    return this.http.get<ModelAzureStorageInfo>(this.endpoints.token.getSASAppendToken(fileName)).pipe(
      map((result: ModelAzureStorageInfo) => {
        this.appMonitor.logEvent(this, 'Blob storage append subscription client initialized', { fileName });
        return new BlobServiceClient(`${result.storageUri}${result.storageAccessToken}`);
      })
    );
  }

  loadCampaignFile(organizationId: string, campaignId: string): Observable<ModelCampaign> {
    return this.http.get<ModelCampaign>(this.endpoints.campaigns.campaignConfig(organizationId, campaignId));
  }

  loadRedirectCampaignFile(organizationId: string, campaignId: string): Observable<ModelRedirectCampaign> {
    return this.http.get<ModelRedirectCampaign>(
      this.endpoints.campaigns.campaignRedirectFile(organizationId, campaignId),
      { headers: { 'Cache-Control': 'no-cache' } }
    );
  }

  loadCampaignSubscriptionFile(organizationId: string, campaignId: string): Observable<ModelSubscriptionRecord[]> {
    return this.http
      .get(this.endpoints.campaigns.campaignSubscriptionFile(organizationId, campaignId), {
        responseType: 'text',
        headers: { 'Cache-Control': 'no-cache' }
      })
      .pipe(
        map((response: string) => {
          // Remove the last character and replace it with "]"
          const modifiedResponse = response.length === 1 ? response + ']' : response.slice(0, -1) + ']';
          // Parse the modified string into a JSON object
          try {
            const parsed = JSON.parse(modifiedResponse) as unknown[];
            return parsed.map(raw => this.normalizeRecord(raw));
          } catch (error) {
            this.appMonitor.logException(this, 'Error parsing Campaign subscription file', {
              organizationId,
              campaignId,
              error
            });
            throw new Error('Invalid JSON format after modification of campaign subscription file');
          }
        })
      );
  }

  loadOrganizationSubscriptionFile(organizationId: string): Observable<PushSubscription[]> {
    return this.http
      .get(this.endpoints.organization.organizationSubscriptionFile(organizationId), { responseType: 'text' })
      .pipe(
        map((response: string) => {
          // Remove the last character and replace it with "]"
          const modifiedResponse = response.length === 1 ? response + ']' : response.slice(0, -1) + ']';
          // Parse the modified string into a JSON object
          try {
            return JSON.parse(modifiedResponse);
          } catch (error) {
            this.appMonitor.logException(this, 'Error parsing Campaign subscription file', {
              organizationId,
              error
            });
            throw new Error('Invalid JSON format after modification of campaign subscription file');
          }
        })
      );
  }

  loadFile<T>(fileName: string): Observable<T> {
    return this.http.get<T>(`${this.endpoints.azureStorage.base}/${fileName}`, {
      headers: { 'Cache-Control': 'no-cache' }
    });
  }

  loadMenuFile(organizationId: string, campaignId: string): Observable<ModelMenu> {
    return this.http.get<ModelMenu>(this.endpoints.menu.loadMenuFile(organizationId, campaignId));
  }

  async listCampaignsForOrganization(organizationId: string): Promise<ModelCampaignListDetails[]> {
    if (this.campaigns.length > 0) {
      this.appMonitor.logEvent(this, 'Returning cached campaigns', { organizationId });
      return this.campaigns;
    }
    this.appMonitor.logEvent(this, 'Listing campaigns for organization from storage', { organizationId });
    await this.listBlobHierarchical(organizationId);
    return this.campaigns;
  }

  /**
   * Adds a campaign to the master campaign list if it doesn't already exist.
   *
   * @param campaignMetaData - The campaign metadata containing campaign information including campaign ID
   * @returns void - This method does not return a value
   *
   * @remarks
   * - If a campaign with the same ID already exists in the list, the method logs an event and returns early
   * - If the campaign is new, it creates a new campaign list item from the metadata and adds it to the campaigns array
   * - This method is used to ensure that the campaign list is updated with new campaigns without querying the storage again
   * - It is typically called after a new campaign is created or when campaign metadata is updated
   */
  addCampaignToMasterList(campaignMetaData: ModelCampaignMetaData): void {
    const campaign = this.campaigns.find((c) => c.id === campaignMetaData.campaignid);
    if (campaign) {
      this.appMonitor.logEvent(this, 'Campaign is already in list', { campaignId: campaignMetaData.campaignid });
      return;
    }
    const newCampaign = this.createModelCampaignListItemFromMetadata(campaignMetaData);
    this.campaigns.push(newCampaign);
  }

  private async listBlobHierarchical(directory: string, pageSize = 25, virtualHierarchyDelimiter = '/') {
    // some options for filtering list
    const listOptions: ContainerListBlobsOptions = {
      includeMetadata: true,
      includeSnapshots: false,
      includeTags: false,
      includeVersions: false,
      prefix: directory
    };

    for await (const response of this.containerClient
      .listBlobsByHierarchy(virtualHierarchyDelimiter, listOptions)
      .byPage({ maxPageSize: pageSize })) {
      const segment = response.segment;

      if (segment.blobPrefixes) {
        // Do something with each virtual folder
        for (const prefix of segment.blobPrefixes) {
          // build new virtualHierarchyDelimiter from current and next
          await this.listBlobHierarchical(directory, pageSize, `${virtualHierarchyDelimiter}${prefix.name}`);
        }
      }
      this.appMonitor.logEvent(this, 'Processing blobs in segment', { responseCount: segment.blobItems.length });
      for (const blob of response.segment.blobItems) {
        this.addBlob(blob);
      }
    }
  }

  async filterBlobsByMetadata(
    organizationId: string,
    filters: { fromDate?: Date; toDate?: Date; name?: string }
  ): Promise<ModelCampaignListDetails[]> {
    const campaigns = await this.listCampaignsForOrganization(organizationId);

    return campaigns.filter((campaign) => {
      const matchesName = !filters.name || campaign.name.toLowerCase().includes(filters.name.toLowerCase());
      const matchesStartDate = !filters.fromDate || campaign.fromDate >= filters.fromDate;

      return matchesName && matchesStartDate;
    });
  }

  private addBlob(blob: BlobItem) {
    const campaignId = (blob?.metadata as ModelCampaignMetaData)?.campaignid;
    if (campaignId) {
      if (blob.name.endsWith(this.appConfig.azureStorage.subscriptionsFilename)) {
        this.appMonitor.logEvent(this, 'Skipping subscription file', { blobName: blob.name });
        return;
      }
      let campaign = this.campaigns.find((campaigns) => campaigns.id === campaignId);
      if (!campaign) {
        const blobMetaData = blob?.metadata as ModelCampaignMetaData;
        campaign = this.createModelCampaignListItemFromMetadata(blobMetaData);
        this.campaigns.push(campaign);
      }
      campaign.blobItems.push(blob);
    }
  }

  createModelCampaignListItemFromMetadata(metaData: ModelCampaignMetaData): ModelCampaignListDetails {
    return {
      id: metaData.campaignid,
      name: metaData.name,
      url: metaData.url,
      description: metaData.description,
      fromDate: new Date(metaData.validfrom),
      toDate: new Date(metaData.validto),
      createdBy: metaData.uploadedBy,
      blobItems: [],
      viewer: metaData.viewer,
      isArchived: false
    };
  }

  getBlobItemAbsoluteURL(item: BlobItem): string {
    return `${this.appConfig.azureStorage.storageURL}/${this.appConfig.azureStorage.baseContainerName}/${item.name}`;
  }

  getBlobItemFileNameFromAbsoluteURL(url: string): string {
    return url.replace(
      `${this.appConfig.azureStorage.storageURL}/${this.appConfig.azureStorage.baseContainerName}/`,
      ''
    );
  }

  /**
   * Creates a subscription file as an append blob in Azure Storage for a specific campaign.
   *
   * @param organizationId - The unique identifier for the organization
   * @param campaignId - The unique identifier for the campaign
   * @param blobMetaData - Metadata object containing campaign information to be attached to the blob
   * @returns A Promise that resolves to true if the operation failed (status > 299 and not 409),
   *          false if the blob was successfully created or already existed
   *
   * @remarks
   * - Sets the blob metadata index to '-2'
   * - Creates the blob with JSON content type
   * - If the blob is newly created, initializes it with an opening bracket '['
   * - Returns true on failure (unexpected status codes) and false on success
   * - Status code 409 (Conflict) is treated as success since it indicates the blob already exists
   */
  async createSubscriptionFile(
    organizationId: string,
    campaignId: string,
    blobMetaData: ModelCampaignMetaData
  ): Promise<boolean> {
    try {
      this.appMonitor.logEvent(this, 'Creating subscription file', { organizationId, campaignId });

      const blobServiceClient = await this.initializeBlobStorageCRUDClient().firstValue();
      const containerClient = blobServiceClient.getContainerClient(this.appConfig.azureStorage.baseContainerName);

      const directory = `${organizationId}/${campaignId}`;
      const blobName = `${directory}/${this.appConfig.azureStorage.subscriptionsFilename}`;
      const appendBlobClient = containerClient.getAppendBlobClient(blobName);

      // Set metadata with index -2 as mentioned in the remarks
      const metadata = {
        ...blobMetaData,
        index: '-2'
      };

      // Create the append blob with JSON content type
      const createOptions = {
        blobHTTPHeaders: {
          blobContentType: 'application/json'
        },
        metadata: metadata
      };

      const response = await appendBlobClient.create(createOptions);

      // Check if creation was successful
      if (response._response.status > 299 && response._response.status !== 409) {
        this.appMonitor.logException(this, 'Failed to create subscription file', {
          organizationId,
          campaignId,
          status: response._response.status
        });
        return true; // Return true on failure as per documentation
      }

      // If blob was newly created (status 201), initialize with opening bracket
      if (response._response.status === 201) {
        await appendBlobClient.appendBlock('[', 1);
        this.appMonitor.logEvent(this, 'Subscription file created and initialized', {
          organizationId,
          campaignId,
          blobName
        });
      } else {
        this.appMonitor.logEvent(this, 'Subscription file already exists', {
          organizationId,
          campaignId,
          blobName
        });
      }

      return false; // Return false on success as per documentation
    } catch (error) {
      this.appMonitor.logException(this, 'Error creating subscription file', {
        organizationId,
        campaignId,
        error
      });
      return true; // Return true on failure
    }
  }

  /**
   * Uploads multiple files to Azure Blob Storage in parallel for a specific campaign.
   *
   * This method processes an array of files concurrently, uploading each to the specified
   * organization and campaign directory within the configured Azure Storage container.
   * It handles metadata assignment, content type detection, and provides detailed results
   * for both successful and failed uploads.
   *
   * @param files - Array of file objects containing filename, File data, and metadata
   * @param files[].filename - The desired filename for the uploaded file
   * @param files[].file - The File object to be uploaded
   * @param files[].metadata - Key-value pairs of metadata to associate with the file
   * @param organizationId - The unique identifier for the organization
   * @param campaignId - The unique identifier for the campaign
   * @param containerName - Optional container name (defaults to base container from config)
   *
   * @returns Promise that resolves to ModelAzureStorageFileUploadResponse containing:
   *   - successful: Array of filenames that were uploaded successfully
   *   - failed: Array of objects with file names and their corresponding error details
   *
   * @throws Will not throw directly, but captures and returns individual file upload errors
   *
   * @example
   * ```typescript
   * const files = [
   *   { filename: 'doc1.pdf', file: pdfFile, metadata: { type: 'document' } },
   *   { filename: 'img1.jpg', file: imageFile, metadata: { category: 'photo' } }
   * ];
   *
   * const result = await service.uploadFilesParallel(files, 'org123', 'campaign456');
   * console.log(`${result.successful.length} files uploaded successfully`);
   * console.log(`${result.failed.length} files failed to upload`);
   * ```
   */
  async uploadFilesParallel(
    files: { filename: string; file: File; metadata: Record<string, string> }[],
    organizationId: string,
    campaignId: string,
    containerName = this.appConfig.azureStorage.baseContainerName
  ): Promise<ModelAzureStorageFileUploadResponse> {
    this.appMonitor.logEvent(this, 'Starting parallel file upload', {
      containerName,
      organizationId,
      campaignId,
      fileCount: files.length
    });
    const directory = `${containerName}/${organizationId}/${campaignId}`;

    const blobServiceClient = await this.initializeBlobStorageAnonymousCRUDClient(
      containerName,
      directory
    ).firstValue();
    this.appMonitor.logEvent(this, 'Blob service client initialized', { containerName });
    const containerClient = blobServiceClient.getContainerClient(containerName);
    this.appMonitor.logEvent(this, 'Container client obtained', { containerName });

    this.appMonitor.logEvent(this, 'Uploading files in parallel', {
      fileCount: files.length,
      organizationId,
      campaignId
    });
    const uploadPromises = files.map(async (fileItem) => {
      try {
        const directory = `${organizationId}/${campaignId}`;
        const blobName = `${directory}/${fileItem.filename}`;
        const blockBlobClient = containerClient.getBlockBlobClient(blobName);

        const uploadOptions = {
          blobHTTPHeaders: {
            blobContentType: fileItem.file.type || 'application/octet-stream'
          },
          metadata: fileItem.metadata || {},
          overwrite: true // Explicitly set to overwrite existing files
        };

        await blockBlobClient.uploadData(fileItem.file, uploadOptions);
        return { success: true, fileName: fileItem.filename };
      } catch (error) {
        this.appMonitor.logException(this, 'Failed to upload file', { fileName: fileItem.filename, error });
        return { success: false, fileName: fileItem.filename, error };
      }
    });

    const results = await Promise.allSettled(uploadPromises);

    const successful: string[] = [];
    const failed: { file: string; error: any }[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value.success) {
        successful.push(result.value.fileName);
      } else {
        const fileName = files[index].filename;
        const error = result.status === 'rejected' ? result.reason : result.value.error;
        failed.push({ file: fileName, error });
      }
    });

    this.appMonitor.logEvent(this, 'Parallel upload completed', {
      totalFiles: files.length,
      successful: successful.length,
      failed: failed.length
    });

    return { successful, failed };
  }

  async appendSubscription(directory: string, record: ModelSubscriptionRecord): Promise<void> {
    this.appMonitor.logEvent(this, 'Adding subscription', { topicCount: record.topicIds.length });
    const containerName = `${this.appConfig.azureStorage.baseContainerName}`;
    const blobPath = `${directory}/${this.appConfig.azureStorage.subscriptionsFilename}`;

    const blobClient = await this.initializeBlobStorageAppendSubscriptionClient(blobPath).firstValue();
    const containerClient = blobClient.getContainerClient(containerName);
    const appendClient = containerClient.getAppendBlobClient(blobPath);

    const chunk = `${JSON.stringify(record)},`;
    const byteLength = new Blob([chunk]).size;
    await appendClient.appendBlock(chunk, byteLength);
  }

  private normalizeRecord(raw: unknown): ModelSubscriptionRecord {
    const candidate = raw as Partial<ModelSubscriptionRecord>;
    if (candidate && Array.isArray(candidate.topicIds)) {
      return candidate as ModelSubscriptionRecord;
    }
    // legacy element = bare PushSubscription
    return ModelInitializer.SubscriptionRecord({
      topicIds: [],
      topicsSnapshot: [],
      pushSubscription: raw as PushSubscription
    });
  }
}
